import { memo } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { useApolloClient } from "@apollo/client";
import { Divider, Space } from "antd";
import { IS_SSO_BRIDGE } from "constants/app.constants";
import { logoutUser } from "store/features/auth/authSlice";
import { resetSlices } from "store/rootReducer";
import { usePanelStore } from "stores/panelStore";
import { useWellCardPanelStore } from "stores/wellCardPanel";
import styled from "styled-components/macro";

import { useUser } from "hooks";
import useBetaFeatures from "hooks/useBetaFeatures";

import { AboutMenuItem } from "components/about";
import { SystemStatusMenuItem } from "components/system-status";

import UserMenuItem from "./UserMenuItem";
import UserProfileHeader from "./UserProfileHeader";

interface UserMenuProps {
  onClose?: () => void;
}
function UserMenu({ onClose = null }: UserMenuProps) {
  const client = useApolloClient();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const { user, isAdmin } = useUser();
  const { hasFeature } = useBetaFeatures();

  const signout = () => () => {
    dispatch(logoutUser());
    resetSlices(dispatch);
    usePanelStore.getState().closePanel();
    // The card is held in memory, and this store outlives the sign-out — its
    // hydrate is a no-op while the dashboard is the same — so without this the
    // next user would find the card exactly as it was left.
    useWellCardPanelStore.getState().reset();
    sessionStorage.clear();

    client.resetStore();
    queryClient.clear();
    history.push("/login");
  };

  const { openPanel } = usePanelStore();

  const openUserSettings = () => {
    openPanel("userSettings", "Profile");
    onClose && onClose();
  };

  const openOrganizationSettings = () => {
    openPanel("organizationSettings", "OrganizationProfile");
    onClose && onClose();
  };

  const openApplicationTools = () => {
    openPanel("applicationTools", "Subscriptions");
    onClose && onClose();
  };

  if (!history || !user) return <>Loading your info...</>;

  return (
    <Wrapper direction="vertical" split={<StyledDivider />}>
      <UserMenuItem>
        <UserProfileHeader />
      </UserMenuItem>

      <UserMenuItem testId="user-menu-settings" onClick={() => openUserSettings()}>
        Your Settings
      </UserMenuItem>

      {isAdmin && (
        <UserMenuItem onClick={() => openOrganizationSettings()}>
          Your Organization Settings
        </UserMenuItem>
      )}

      {hasFeature("App Tools") && (
        <UserMenuItem onClick={() => openApplicationTools()}>
          Application Tools
        </UserMenuItem>
      )}

      <SystemStatusMenuItem />

      <AboutMenuItem onClose={onClose} />

      {/* Under the Novi Auth bridge the session cookie is HttpOnly and owned by
          the edge — EVA can't end it, so signing out happens outside EVA. */}
      {!IS_SSO_BRIDGE && <UserMenuItem onClick={signout()}>Sign out</UserMenuItem>}
    </Wrapper>
  );
}

export default memo(UserMenu);

const Wrapper = styled(Space)`
  width: 30rem;
  color: var(--color-text-60);
  margin: -4px -16px;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;
